// JPEG / EXIF magic byte codes used throughout readJpegOrientation:
//
// | Hex          | ASCII  | Meaning                                                              |
// | ------------ | ------ | ------------------------------------------------------------------- |
// | `0xffd8`     | —      | JPEG SOI (Start Of Image) marker — every JPEG begins with this      |
// | `0xffe1`     | —      | APP1 application segment marker — the segment that carries EXIF      |
// | `0x45786966` | `Exif` | EXIF identifier string at the start of the APP1 payload             |
// | `0x4949`     | `II`   | TIFF byte order = little-endian (Intel)                             |
// | `0x4d4d`     | `MM`   | TIFF byte order = big-endian (Motorola)                             |
// | `0x0112`     | —      | EXIF tag ID for Orientation (the tag we read)                       |
// | `0xff00`     | —      | High-byte mask: every JPEG marker starts with `0xff`, so            |
// |              |        | `(marker & 0xff00) === 0xff00` validates the marker sequence        |
//
// Orientation tag values (0x0112) run 1–8; 1 = normal/upright. Anything outside
// 1–8 is treated as 1. See the EXIF spec for the full rotation/flip meanings.
//
// Reads the EXIF Orientation tag (0x0112) from a JPEG. Returns 1 (normal) for
// non-JPEG data, a missing tag, or any malformed/truncated input.
export function readJpegOrientation(data: ArrayBuffer | Uint8Array): number {
    const view = data instanceof Uint8Array
        ? new DataView(data.buffer, data.byteOffset, data.byteLength)
        : new DataView(data);
    const length = view.byteLength;

    if (length < 2 || view.getUint16(0, false) !== 0xffd8) return 1; // not a JPEG (no SOI)

    let offset = 2;
    while (offset + 4 <= length) {
        const marker = view.getUint16(offset, false);
        offset += 2;

        if (marker === 0xffe1) { // APP1 — may hold EXIF
            if (offset + 8 > length) return 1;
            if (view.getUint32(offset + 2, false) !== 0x45786966) return 1; // "Exif"

            const tiff = offset + 8;
            if (tiff + 8 > length) return 1; // not enough bytes for the TIFF header
            const byteOrder = view.getUint16(tiff, false);
            if (byteOrder !== 0x4949 && byteOrder !== 0x4d4d) return 1;
            const little = byteOrder === 0x4949;

            const ifd = tiff + view.getUint32(tiff + 4, little);
            if (ifd + 2 > length) return 1;

            const entries = view.getUint16(ifd, little);
            for (let i = 0; i < entries; i++) {
                const entry = ifd + 2 + i * 12;
                if (entry + 12 > length) return 1;
                if (view.getUint16(entry, little) === 0x0112) {
                    const value = view.getUint16(entry + 8, little);
                    return value >= 1 && value <= 8 ? value : 1;
                }
            }
            return 1;
        }

        if ((marker & 0xff00) !== 0xff00) return 1; // not a valid marker sequence
        if (offset + 2 > length) return 1;
        offset += view.getUint16(offset, false); // skip this segment
    }
    return 1;
}

export interface NormalizeOptions {
    // When true, a rotation flag alone (orientation !== 1) forces a re-encode so the correct
    // orientation is baked into the pixels. When false, orientation is only baked as a side effect
    // of a re-encode triggered by resizing or format conversion — an upright-enough, fitting image
    // is left untouched.
    normalizeOrientation: boolean;
    maxWidth: number;
    maxHeight: number;
    quality: number;
    // false = keep the source format (PNG stays PNG, transparency survives); true = convert to
    // JPEG. A format-changing conversion forces a re-encode; preserving the format does not.
    convertFormat: boolean;
}

export interface ResizeTarget { targetWidth: number; targetHeight: number; }

// Decides whether an image needs downscaling to fit within the configured caps.
// `width`/`height` are display-space dimensions (the decoder bakes EXIF orientation
// before we get here), so no axis swap is needed. A cap of 0 (or less) means that axis
// is unconstrained: supplying only maxWidth caps width and scales height proportionally,
// and vice versa. Returns null when the image already fits or both caps are unset.
export function decideResize(width: number, height: number, opts: NormalizeOptions): ResizeTarget | null {
    const maxWidth = opts.maxWidth > 0 ? opts.maxWidth : Infinity;
    const maxHeight = opts.maxHeight > 0 ? opts.maxHeight : Infinity;
    if (maxWidth === Infinity && maxHeight === Infinity) return null; // resizing disabled
    if (width <= maxWidth && height <= maxHeight) return null; // already fits

    // Both axes share this scale, so aspect ratio is preserved. An unconstrained axis
    // contributes Infinity to the min and never drives the result.
    const scale = Math.min(maxWidth / width, maxHeight / height);
    return { targetWidth: Math.round(width * scale), targetHeight: Math.round(height * scale) };
}

const isImageBlob = (file: File | Blob): boolean =>
    typeof file.type === 'string' && file.type.indexOf('image/') === 0;

function encodeCanvas(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
    return new Promise(resolve => canvas.toBlob(blob => resolve(blob), type, quality));
}

// Maps an image MIME type to a stable upload filename. Normalized uploads and camera captures are
// bare Blobs with no name of their own, and the server routes processing off the extension, so the
// name must match the actual bytes (a re-encoded PNG mustn't be sent as image.jpg).
export function blobFileName(type: string): string {
    const ext = type === 'image/png' ? 'png'
        : type === 'image/webp' ? 'webp'
        : type === 'image/gif' ? 'gif'
        : 'jpg';
    return `image.${ext}`;
}

// Normalizes an image for upload. An upright image that already fits and whose format would be
// preserved is passed through BYTE-FOR-BYTE (the original file reference is returned). Otherwise it
// is re-encoded, which strips ALL EXIF — including the Orientation tag that makes photos print
// rotated in viewers that ignore it (e.g. PhantomJS badge rendering in the PACS) — and bakes
// orientation into the pixels. A re-encode is triggered by any of: a rotation flag when
// `normalizeOrientation` is set, the image exceeding the configured caps, or `convertFormat`
// changing the output format. The output is JPEG when `convertFormat` is set, otherwise the source
// format (so PNG transparency survives). Returns the original file when it isn't an image, the
// browser can't decode it (e.g. HEIC) or lacks createImageBitmap, or encoding fails — in which case
// behaviour is no worse than before this normalisation existed.
export async function normalizeImageForUpload(file: File | Blob, opts: NormalizeOptions): Promise<File | Blob> {
    if (!isImageBlob(file) || typeof createImageBitmap !== 'function') return file;

    try {
        // Orientation only lives in JPEG EXIF here; other formats are treated as upright (1).
        const orientation = file.type === 'image/jpeg'
            ? readJpegOrientation(await file.arrayBuffer())
            : 1;

        // Decode with EXIF orientation baked into the pixels for EVERY format, so the bitmap's
        // own dimensions are already in display space (rotated non-JPEGs aren't squashed) and a
        // single decode covers both orientation and sizing.
        const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' } as unknown as ImageBitmapOptions);
        try {
            const resize = decideResize(bitmap.width, bitmap.height, opts);

            const orientationOk = !opts.normalizeOrientation || orientation === 1;
            const formatPreserved = !opts.convertFormat || file.type === 'image/jpeg';
            if (orientationOk && !resize && formatPreserved) return file; // upright, fits, same format => passthrough

            const target = resize ?? { targetWidth: bitmap.width, targetHeight: bitmap.height };

            const canvas = document.createElement('canvas');
            canvas.width = target.targetWidth;
            canvas.height = target.targetHeight;
            const context = canvas.getContext('2d');
            if (!context) return file;
            context.imageSmoothingQuality = 'high'; // better downscaling of large photos
            context.drawImage(bitmap, 0, 0, target.targetWidth, target.targetHeight);
            const blob = await encodeCanvas(canvas, opts.convertFormat ? 'image/jpeg' : file.type, opts.quality);
            return blob || file;
        } finally {
            if (typeof bitmap.close === 'function') bitmap.close();
        }
    } catch {
        return file; // enhancement, not a gate — never worse than today's behaviour
    }
}
